export function leftpad(value: number | string = 0, size: number) {
  let s = value + '';
  while (s.length < size) s = '0' + s;
  return s;
}

export function rightpad(value: number | string = 0, size: number) {
  let s = value + '';
  while (s.length < size) s = s + '0';
  return s;
}

export function formatPercent(value: number | string) {
  if (typeof value === 'undefined' || value === null) return '';
  const [left, right] = value.toString().split('.');
  return `${leftpad(left || 0, 2)}.${rightpad(right || 0, 2)}`;
}

export const formatTime = (value: number | string) => {
  return !!value ? new Date(value).toLocaleTimeString() : '';
};

export const formatDate = (value: number | string) => {
  return !!value ? new Date(value).toLocaleDateString() : '';
};

export const formatDateTime = (value: number | string) => {
  return !!value ? `${formatDate(value)} ${formatTime(value)}` : '';
};

/**
 * Prefer formatMinorUnits below for anything out of the statistics pipeline: it owns the
 * division by 100 and takes a nullable code. This one takes major units already divided.
 *
 * The try/catch is the same guard for the same reason. Intl throws RangeError on any
 * code it does not recognise, currency_code is nullable free text, and every caller was
 * passing a value read straight out of it -- so 'nill' (1.27M rows) and 'EU4' (621) threw
 * from inside a component's render and took the whole Performance table down rather than
 * mis-formatting one cell.
 */
export function formatCurrency(value: number | string, currency: string, decimals: number = 2) {
  const locale = navigator.language || navigator.languages[0];
  const amount = +(+value).toFixed(decimals) || 0;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return `${new Intl.NumberFormat(locale).format(amount)} ${currency}`;
  }
}

export function formatNumber(value: number | string) {
  const locale = navigator.language || navigator.languages[0];
  const formatter = new Intl.NumberFormat(locale);
  return formatter.format(+value || 0);
}

/**
 * Money out of the statistics pipeline is in minor units (öre, cents): that is
 * how the tracking payload arrives and the only integer-safe way to sum it.
 *
 * Both helpers below own the division by 100, so it happens in exactly one
 * place. It previously happened in two, inconsistently, and the dashboard tile
 * skipped it entirely and rendered öre under a SEK label.
 *
 * Intl throws RangeError on an unrecognised currency code, and the code is a
 * nullable free-text column, so an unknown one degrades to a plain number with
 * the raw code rather than taking the page down.
 */
function formatMoney(value: number, currency: string | null, compact: boolean) {
  const locale = navigator.language || navigator.languages[0];
  const options: Intl.NumberFormatOptions = compact
    ? { notation: 'compact', maximumFractionDigits: 1 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };

  const amount = (Number.isFinite(value) ? value : 0) / 100;

  if (currency) {
    try {
      return new Intl.NumberFormat(locale, { ...options, style: 'currency', currency }).format(amount);
    } catch {
      // Unrecognised code: fall back to a plain number and name the code
      // explicitly, rather than printing a bare figure with no unit.
      return `${new Intl.NumberFormat(locale, options).format(amount)} ${currency}`;
    }
  }
  return new Intl.NumberFormat(locale, options).format(amount);
}

/** Exact amount, two decimals. For tooltips and anywhere a figure is read off. */
export const formatMinorUnits = (value: number, currency: string | null) => formatMoney(value, currency, false);

/** Abbreviated amount. For metric tiles, where the magnitude is the point. */
export const formatMinorUnitsCompact = (value: number, currency: string | null) => formatMoney(value, currency, true);

/**
 * Period-over-period change, as a percentage of the previous value.
 *
 * Null when there is nothing to compare against: no preceding window at all
 * ('alltime'), or a previous value of zero, where any increase is division by
 * zero and "+100%" would be a fabrication rather than a measurement.
 */
export function percentChange(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined) return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export const formatPercentage = (value: string | number) => (value ? `${(+value).toFixed(2)}%` : '00.00%');

export const formatDiff = (value: string | number, isPercent = false, decimals = false) =>
  value ? `${+value > 0 ? '+' : ''}${(+value).toFixed(decimals ? 2 : 0)}${isPercent ? '%' : ''}` : '';

export function daysAgo(first: number, last: number) {
  return Math.ceil(Math.abs(first - last) / (1000 * 60 * 60 * 24));
}

export function formatByThousands(value: number) {
  const locale = navigator.language || navigator.languages[0];
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (value >= 1000000) {
    return `${formatter.format(value / 1000000)}M`;
  }
  if (value >= 1000) {
    return `${formatter.format(value / 1000)}K`;
  }
  return formatter.format(value);
}
